import React from 'react';
import { Trans } from 'react-i18next';

import ContentTimestamp from 'components/ContentTimestamp';
import { LinkIfHref } from 'components/Link';

import styles from './styles.module.scss';

/**
 * ContentTimestampWithSource component.
 *
 * Displays content creation and publication timestamps, and optionally the source organization.
 *
 * @param {object} props
 * @param {string} props.dateCreated - The creation date of the content.
 * @param {string} props.datePublished - The publication date of the content.
 * @param {boolean} [props.showCreatedDate=false] - Whether to show the created date.
 * @param {string} [props.delimiter=', '] - Delimiter between dates.
 * @param {boolean} [props.meta=true] - Whether to include meta information.
 * @param {boolean} [props.hideMicroData=true] - Whether to hide microdata.
 * @param {import('lib/graphqlTypeDefs.mjs').EmbeddedOrganization} [props.source] - Whether to show the source.
 * @param {string} [props.orgName] - Name of the source organization.
 * @param {string} [props.orgUrl] - URL of the source organization.
 * @param {string} [props.testId='content-timestamp-with-source'] - Test ID for the component.
 */
export const ContentTimestampWithSource = ({
  dateCreated,
  datePublished,
  delimiter = ', ',
  hideMicroData = true,
  meta = true,
  orgName,
  orgUrl,
  showCreatedDate = false,
  source,
  testId = 'content-timestamp-with-source',
}) => (
  <div
    className={styles.contentTimestampWithSource}
    data-testid={testId}
  >
    <ContentTimestamp
      dateCreated={dateCreated}
      datePublished={datePublished}
      delimiter={delimiter}
      hideMicroData={hideMicroData}
      meta={meta}
      showCreatedDate={showCreatedDate}
      testId={`${testId}__timestamp`}
    />
    {source && orgName && (
      <span data-testid={`${testId}__source`}>
        {(dateCreated || datePublished) ? ' / ' : ''}
        <Trans>
            Source
        </Trans>
        {': '}
        <LinkIfHref
          className={styles.contentSourceLink}
          href={orgUrl}
        >
          {orgName}
        </LinkIfHref>
      </span>
    )}
  </div>
);
